/**
 * Sparkline — тэнхлэггүй жижиг график.
 *
 * SVG-ийн координат нь `d` атрибутад ордог тул `style` шаардахгүй.
 * Өнгө нь `var(--color-*)` — харанхуй горимд өөрөө дагаж солигдоно.
 *
 * viewBox тогтмол 100×40, `preserveAspectRatio="none"` — эцэг элемент
 * ямар ч өргөнтэй байсан дүүргэнэ. Шугамын зузаан нь
 * `vector-effect="non-scaling-stroke"`-оор гажихгүй.
 *
 * `smooth` + `animate` — дашбоардын KPI (dark glass): monotone муруй,
 * доошоо бүдгэрэх дүүргэлт, ачаалагдахдаа зүүнээс баруун тийш зурагдана.
 */

import { useId } from 'react';
import { monotonePath } from '../lib/monotone';
import { useTween } from '../lib/use-tween';

interface SparkGeometry {
  line: string;
  area: string;
  last: { x: number; y: number } | null;
}

/**
 * Цэгүүдийг зам болгоно. Дээд/доод талд 25% зай үлдээнэ — эс тэгвээс
 * оргил нь хүрээнд наалдаж таслагдсан мэт харагдана.
 */
function sparkGeometry(values: number[], smooth: boolean): SparkGeometry {
  const hi = values.length ? Math.max(...values) : 0;
  const lo = values.length ? Math.min(...values) : 0;
  const pad = (hi - lo) * 0.25 || 0.1;
  const yLo = lo - pad;
  const yHi = hi + pad;

  const pts = values.map((v, i) => ({
    x: values.length > 1 ? (i / (values.length - 1)) * 100 : 0,
    y: 40 - ((v - yLo) / (yHi - yLo)) * 40,
  }));

  const line = smooth
    ? monotonePath(pts)
    : pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

  return {
    line,
    area: pts.length ? `${line} L100 40 L0 40 Z` : '',
    last: pts.at(-1) ?? null,
  };
}

export function Sparkline({
  values,
  label,
  area = true,
  dot = true,
  tone = 'accent',
  smooth = false,
  animate = false,
  className = '',
}: {
  values: number[];
  /** Дэлгэц уншигчид — график юуг харуулж байгаа */
  label: string;
  area?: boolean;
  dot?: boolean;
  tone?: 'accent' | 'alert';
  /** Monotone муруй — тэгээс доош цухуйхгүй */
  smooth?: boolean;
  /** Ачаалагдахдаа зүүнээс баруун тийш зурагдана (1.4s) */
  animate?: boolean;
  className?: string;
}) {
  const id = useId();
  // Анимацгүй үед зорилт 0 — tween огт эхлэхгүй (бусад хуудасны олон sparkline)
  const progress = useTween(animate ? 1 : 0);
  if (values.length < 2) return null;

  const g = sparkGeometry(values, smooth);
  const stroke = tone === 'alert' ? 'var(--color-alert)' : 'var(--color-accent-700)';
  const fill = tone === 'alert' ? 'var(--color-alert)' : 'var(--color-accent)';
  const reveal = animate ? progress : 1;

  return (
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      className={`size-full overflow-visible ${className}`}
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={`${id}-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={fill} stopOpacity="0.4" />
          <stop offset="1" stopColor={fill} stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <rect x="-4" y="-10" width={108 * reveal} height="60" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        {area ? <path d={g.area} fill={`url(#${id}-fill)`} /> : null}
        <path
          d={g.line}
          fill="none"
          stroke={stroke}
          strokeWidth={smooth ? 2.5 : 0.9}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
      {dot && g.last && reveal === 1 ? (
        <circle
          cx={g.last.x.toFixed(2)}
          cy={g.last.y.toFixed(2)}
          r="1.4"
          fill={stroke}
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}
